// ABOUTME: The header's one Work button: it spins while jobs run (the bell carries the one count
// badge). Beside a chat on a wide
// screen it shows or hides the docked Work panel; elsewhere it opens the same Work view (jobs,
// exact preview, scheduled posts) as a popover, a sheet on phones. It toasts when a job finishes
// or fails, so a finished job is noticed with the view closed.

import { useEffect, useRef } from 'react'
import { Loader2, PanelRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { useJobsStore } from '@/stores/useJobsStore'
import { useUiStore } from '@/stores/useUiStore'
import { breakpoints, useIsMobile, useMediaQuery } from '@/hooks/useMediaQuery'
import type { JobStatus } from '@/api/jobs'
import { WorkContent } from './WorkPanel'

export function WorkButton() {
  const jobs = useJobsStore((s) => s.jobs)
  const activeCount = useJobsStore((s) => s.activeCount)
  const showToast = useUiStore((s) => s.showToast)
  const isMobile = useIsMobile('md')
  const wide = useMediaQuery(`(min-width: ${breakpoints.xl}px)`)
  const docked = useUiStore((s) => s.workPanel.docked)
  const collapsed = useUiStore((s) => s.workPanel.isCollapsed)
  const toggleWorkPanel = useUiStore((s) => s.toggleWorkPanel)

  // Toast on terminal transitions so a finished job is noticed even with the
  // panel closed. Keyed on source+id; first sighting just records the status.
  const prevStatusMap = useRef<Map<string, JobStatus>>(new Map())
  useEffect(() => {
    const prev = prevStatusMap.current
    for (const job of jobs) {
      const key = `${job.source}-${job.id}`
      const oldStatus = prev.get(key)
      if (oldStatus && oldStatus !== job.status) {
        if (job.status === 'completed') {
          showToast({ type: 'success', title: 'Job completed', message: job.title })
        } else if (job.status === 'failed') {
          showToast({ type: 'error', title: 'Job failed', message: job.title })
        }
      }
      prev.set(key, job.status)
    }
  }, [jobs]) // eslint-disable-line react-hooks/exhaustive-deps

  const icon =
    activeCount > 0 ? (
      <Loader2 className="size-5 animate-spin" data-testid="work-active" />
    ) : (
      <PanelRight className="size-5" />
    )

  if (docked && wide) {
    return (
      <Button
        variant="ghost"
        size="icon"
        aria-label={collapsed ? 'Show work panel' : 'Hide work panel'}
        aria-pressed={!collapsed}
        className="relative size-9"
        onClick={toggleWorkPanel}
        data-testid="work-button"
      >
        {icon}
      </Button>
    )
  }

  const trigger = (
    <Button
      variant="ghost"
      size="icon"
      aria-label="Work"
      className="relative size-11 md:size-9"
      data-testid="work-button"
    >
      {icon}
    </Button>
  )

  if (isMobile) {
    return (
      <Sheet>
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent side="bottom" className="max-h-[75vh] p-0" data-testid="work-sheet">
          <SheetHeader className="border-b px-4 py-3">
            <SheetTitle className="text-left text-sm font-medium">Work</SheetTitle>
          </SheetHeader>
          <div className="max-h-[60vh] overflow-y-auto p-4">
            <WorkContent />
          </div>
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        className="bg-popover w-96 p-0 shadow-lg"
        data-testid="work-popover"
      >
        <div className="border-b px-4 py-2 text-sm font-medium">Work</div>
        <div className="max-h-[70vh] overflow-y-auto p-4">
          <WorkContent />
        </div>
      </PopoverContent>
    </Popover>
  )
}
